import { useState } from 'react'

import {
  Box,
  GreyGrey30,
  GreyGrey80,
  Primary13Regular,
  SequenceMenu,
  ShadowXs,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '@sequencehq/core-components'
import { dateTimeWithFormat, toMoney } from '@sequencehq/utils'
import { Currency } from '@sequencehq/api'
import { CurrentPayment } from 'Invoices/types'

import { PaymentDetailsDrawer } from './PaymentDetailsDrawer'
import { PaymentOrigin } from './PaymentOrigin'

// Reuses the shared v2 table, matching the neighbouring invoice-detail tables
// (Usage, line items). The trailing kebab column mirrors the schedule table.
export const PaymentsList = ({
  payments,
  currency
}: {
  payments: CurrentPayment[]
  currency: Currency
}) => (
  <TableContainer
    borderRadius="lg"
    overflow="hidden"
    border={`1px solid ${GreyGrey30}`}
    width="100%"
    boxShadow={ShadowXs}
    data-testid="payments.list"
  >
    <Table variant="v2" width="100%">
      <Thead userSelect="none">
        <Tr>
          <Th width="160px">Amount</Th>
          <Th width="120px">Currency</Th>
          <Th width="140px">Origin</Th>
          <Th textAlign="right">Date</Th>
          <Th width="52px"></Th>
        </Tr>
      </Thead>
      <Tbody>
        {payments.map(payment => (
          <PaymentRow
            key={payment.paymentId}
            payment={payment}
            currency={currency}
          />
        ))}
      </Tbody>
    </Table>
  </TableContainer>
)

// A row opens the payment-details drawer on click; the kebab opens the same
// drawer (its click stops propagation, so it doesn't double-fire the row).
const PaymentRow = ({
  payment,
  currency
}: {
  payment: CurrentPayment
  currency: Currency
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const openDrawer = () => setIsDrawerOpen(true)

  return (
    <>
      <Tr
        onClick={openDrawer}
        data-testid={`payments.row.${payment.paymentId}`}
      >
        <Td>
          <Box
            {...Primary13Regular}
            color={GreyGrey80}
            data-testid={`payments.row.${payment.paymentId}.amount`}
          >
            {toMoney({ value: payment.amount, currency })}
          </Box>
        </Td>
        <Td>
          <Box {...Primary13Regular} color={GreyGrey80}>
            {currency}
          </Box>
        </Td>
        <Td>
          <PaymentOrigin
            origin={payment.origin}
            data-testid={`payments.row.${payment.paymentId}.origin`}
          />
        </Td>
        <Td textAlign="right">
          {dateTimeWithFormat(payment.paidAt, 'MMM d, yyyy')}
        </Td>
        {/* Zero vertical padding so the 24px kebab button doesn't inflate the
            36px row. !important beats the v2 theme's `tbody td` selector —
            same override the schedule table uses. stopPropagation so clicking
            the cell's padding doesn't also trigger the row's open handler. */}
        <Td
          padding="0 12px !important"
          onClick={event => event.stopPropagation()}
        >
          <SequenceMenu
            variant="ghost"
            alignment="bottom-right"
            buttonSize="small"
            data-testid={`payments.row.${payment.paymentId}.menu`}
            items={[
              ({ setIsOpen }) => (
                <SequenceMenu.Item
                  key="view-payment-details"
                  uuid="view-payment-details"
                  label="View payment details"
                  data-testid={`payments.row.${payment.paymentId}.menuItem.view`}
                  onClick={() => {
                    setIsOpen?.(false)
                    openDrawer()
                  }}
                />
              )
            ]}
          />
        </Td>
      </Tr>
      {isDrawerOpen && (
        <PaymentDetailsDrawer
          payment={payment}
          currency={currency}
          onClose={() => setIsDrawerOpen(false)}
        />
      )}
    </>
  )
}
